import { useEffect, useState } from 'react'
import { ApiError } from '../lib/api'
import { mfaApi, type MFAEnrolment, type MyMFA } from '../lib/mfa'
import { ErrorBox } from './DataTable'

// A signed-in person's own second factor for their TPRM password (release
// 3.1): set it up with an authenticator app, make a new set of recovery codes,
// or turn it off where the organisation does not require it. Opened from the
// shield in the sidebar's foot; it is the person's own, so it is not a screen
// any persona's job lists.

export function SecondFactorPanel({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<MyMFA | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [enrolment, setEnrolment] = useState<MFAEnrolment | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  async function load() {
    setError(null)
    try {
      setState(await mfaApi.mine())
    } catch (e) {
      setError(e)
    }
  }
  useEffect(() => {
    void load()
  }, [])

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e)
      if (e instanceof ApiError && e.code === 'MFA_CODE_WRONG') setCode('')
    } finally {
      setBusy(false)
    }
  }

  const start = () =>
    run(async () => {
      setCodes([])
      setEnrolment(await mfaApi.enrol())
    })
  const confirm = () =>
    run(async () => {
      const out = await mfaApi.confirm(code.trim())
      setCodes(out.recoveryCodes)
      setEnrolment(null)
      setCode('')
      await load()
    })
  const fresh = () =>
    run(async () => {
      const out = await mfaApi.newRecoveryCodes(code.trim())
      setCodes(out.recoveryCodes)
      setCode('')
      await load()
    })
  const off = () =>
    run(async () => {
      await mfaApi.disable(code.trim())
      setCode('')
      await load()
    })

  return (
    <section className="panel" style={{ margin: '12px 28px 0' }} data-testid="second-factor" aria-labelledby="second-factor-title" tabIndex={-1}>
      <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 id="second-factor-title" style={{ margin: 0 }}>Two-step sign-in</h2>
        <button type="button" className="ghost" onClick={onClose}>
          Close
        </button>
      </div>
      {state === null && error === null && <p className="muted">Reading your sign-in.</p>}
      {state && !state.available && <p className="muted">{state.why}</p>}
      {state && state.available && state.passwordAllowed === false && <p className="muted">{state.why}</p>}

      {state && state.available && state.passwordAllowed !== false && (
        <>
          {state.enrolled ? (
            <p>
              <strong>On.</strong> Signing in with your TPRM password asks for a code from your authenticator app
              {state.confirmedAt ? ' (set up ' + state.confirmedAt.slice(0, 10) + ')' : ''}.{' '}
              {state.recoveryCodesLeft !== undefined && (
                <span className="muted">
                  {state.recoveryCodesLeft} recovery code{state.recoveryCodesLeft === 1 ? '' : 's'} left.
                </span>
              )}
            </p>
          ) : (
            <p>
              <strong>Off.</strong> Your TPRM password alone signs you in.
              {state.required ? ' Your organisation requires administrators to turn this on.' : ''}
            </p>
          )}

          {!state.enrolled && enrolment === null && (
            <button type="button" className="primary" data-action="tprm.(shell).a11" disabled={busy} onClick={() => void start()}>
              Set up an authenticator app
            </button>
          )}

          {enrolment && (
            <div style={{ marginTop: 12 }}>
              <p className="muted" style={{ fontSize: 13 }}>{enrolment.note}</p>
              {enrolment.qrPng && <img className="mfa-qr" src={enrolment.qrPng} alt="QR code for your authenticator app" width={180} height={180} />}
              <p className="muted" style={{ fontSize: 12 }}>
                Key <code data-testid="mfa-secret">{enrolment.secret}</code>
              </p>
              <label>
                Code from the app
                <input inputMode="numeric" autoComplete="one-time-code" data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
              <button type="button" className="primary" data-action="tprm.(shell).a12" disabled={busy || code.trim() === ''} onClick={() => void confirm()}>
                Turn it on
              </button>
            </div>
          )}

          {state.enrolled && (
            <div style={{ marginTop: 12 }}>
              <label>
                A current code from your app
                <input inputMode="numeric" autoComplete="one-time-code" data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" data-action="tprm.(shell).a13" disabled={busy || code.trim() === ''} onClick={() => void fresh()}>
                  Make new recovery codes
                </button>
                {!state.required && (
                  <button type="button" className="danger" data-action="tprm.(shell).a14" disabled={busy || code.trim() === ''} onClick={() => void off()}>
                    Turn it off
                  </button>
                )}
              </div>
              {state.required && (
                <p className="muted" style={{ fontSize: 12 }}>
                  Your organisation requires it, so it cannot be turned off. For a new phone, ask another administrator to reset it.
                </p>
              )}
            </div>
          )}

          {codes.length > 0 && (
            <div className="note" style={{ marginTop: 12 }} data-testid="mfa-recovery">
              <p>Keep these recovery codes somewhere safe. Each signs you in once if you lose your phone. They are not shown again.</p>
              <ul className="mfa-recovery">
                {codes.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {error !== null && <ErrorBox error={error} what="Your second factor" verb="changed" />}
    </section>
  )
}
